import * as THREE from 'three';
import type { BattleState } from '@somnhold/sim';
import { lookPoint, looksRng, Precip, SkyLook, type OmenCtx, type OmenScene } from './common.ts';

/**
 * Hard frost: a pale, cold light, snow falling (ice motes in the crypt), rime over the ground in patches
 * that glitter as the camera moves, breath puffing from heroes and nightmares, and nightmares that
 * shatter into ice when they fall.
 */

const RIME_VERT = /* glsl */ `
varying vec2 vW;
void main() {
  vec4 w = modelMatrix * vec4(position, 1.0);
  vW = w.xz;
  gl_Position = projectionMatrix * viewMatrix * w;
}`;

const RIME_FRAG = /* glsl */ `
uniform float uTime;
uniform float uRadius;
varying vec2 vW;
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), u.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) { v += a * noise(p); p *= 2.07; a *= 0.5; }
  return v;
}
void main() {
  // Patches of rime, feathered at their edges, thinning out toward the edge of the world.
  float n = fbm(vW * 0.45);
  float fine = fbm(vW * 3.1 + 7.0);
  float rimed = smoothstep(0.5, 0.7, n + fine * 0.15);
  float fade = 1.0 - smoothstep(uRadius * 0.8, uRadius, length(vW));
  // Frost ferns: thin bright veins inside the patches.
  float vein = smoothstep(0.03, 0.0, abs(fbm(vW * 1.6 + 3.0) - 0.5)) * rimed;
  // Glints that wink as the light catches them.
  vec2 cell = floor(vW * 9.0);
  float g = step(0.965, hash(cell)) * rimed * (0.5 + 0.5 * sin(uTime * 3.0 + hash(cell + 3.1) * 60.0));
  vec3 col = mix(vec3(0.78, 0.86, 0.95), vec3(1.0), vein * 0.6 + g);
  float a = (rimed * (0.26 + 0.14 * fine) + vein * 0.2 + g * 0.7) * fade;
  if (a < 0.01) discard;
  gl_FragColor = vec4(col, a);
  #include <colorspace_fragment>
}`;

export const buildFrost = (ctx: OmenCtx, radius: number): OmenScene => {
  const group = new THREE.Group();
  group.name = 'omen-frost';
  const low = ctx.low;
  const rnd = looksRng(0xf205);
  const sky = new SkyLook(ctx, {
    hemi: ['#dbe8ff', '#55607a', 1.05],
    key: ['#dfeaff', 0.85],
    rim: ['#9fdcff', 1.3],
    background: '#9aa6b8',
    fog: '#a9b4c4',
    mist: { low: '#d8e2ee', mid: '#b4c0d0', far: '#97a3b5', alpha: 1.15 },
  });
  const snow = ctx.crypt
    ? new Precip({ count: low ? 80 : 220, box: [11, 5, 11], fall: 0.25, sway: 0.3, size: [0.05, 0.05], color: '#d8f0ff', opacity: 0.7, round: true, additive: true, twinkle: 2 }, 53)
    : new Precip({ count: low ? 320 : 1100, box: [15, 10, 15], fall: 1.15, wind: [0.25, 0.1], sway: 0.35, size: [0.075, 0.075], color: '#ffffff', opacity: 0.85, round: true }, 53);
  group.add(snow.mesh);
  // A draped sheet over the ground: the rime follows every rise of the board.
  const N = low ? 48 : 96;
  const R = radius;
  const geo = new THREE.PlaneGeometry(R * 2, R * 2, N, N).rotateX(-Math.PI / 2);
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) pos.setY(i, ctx.ground(pos.getX(i), pos.getZ(i)) + 0.03);
  geo.computeVertexNormals();
  const rimeMat = new THREE.ShaderMaterial({
    vertexShader: RIME_VERT,
    fragmentShader: RIME_FRAG,
    uniforms: { uTime: { value: 0 }, uRadius: { value: R } },
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -4,
  });
  const rime = new THREE.Mesh(geo, rimeMat);
  rime.renderOrder = 1;
  rime.frustumCulled = false;
  group.add(rime);
  const look = new THREE.Vector3();
  let tickN = 0;

  return {
    group,
    tick: (s: BattleState) => {
      tickN++;
      // Breath in the cold: a little white puff now and then from everyone standing.
      if (tickN % 3 !== 0) return;
      let n = 0;
      for (const h of s.heroes) {
        if (h.downTicks > 0 || rnd() > 0.35) continue;
        const p = ctx.pos(h.id);
        if (p) ctx.vfx.burst(p.clone().setY(p.y + 0.62), { n: 1, color: '#eef6ff', speed: 0.01, up: 0.012, size: 0.16, life: 10, smoke: true, spread: 0.05 });
      }
      for (const e of s.enemies) {
        if (e.hp <= 0 || n > (low ? 4 : 10) || rnd() > 0.18) continue;
        const p = ctx.pos(e.id);
        if (!p) continue;
        n++;
        ctx.vfx.burst(p.clone().setY(p.y + 0.4), { n: 1, color: '#dfefff', speed: 0.01, up: 0.01, size: 0.14, life: 9, smoke: true, spread: 0.05 });
        if (rnd() < 0.4) ctx.vfx.burst(p.clone().setY(p.y + 0.3), { n: 1, color: '#bfe8ff', speed: 0.02, up: 0.02, size: 0.05, life: 8, spread: 0.3 });
      }
    },
    event: (_s, e) => {
      if (e.t !== 'die') return false;
      // Shattered into ice: shards flung out, a cold mist, a ring of rime. The view still draws its own fall.
      const p = ctx.at(e.x, e.y, 0.35);
      ctx.vfx.burst(p, { n: low ? 6 : 14, color: '#dff4ff', speed: 0.13, up: 0.1, size: 0.07, life: 10, gravity: 0.016 });
      ctx.vfx.burst(p, { n: low ? 3 : 6, color: '#8fd0ff', speed: 0.09, up: 0.12, size: 0.11, life: 9, gravity: 0.012 });
      ctx.vfx.burst(p, { n: 2, color: '#e8f2ff', speed: 0.02, up: 0.02, size: 0.45, life: 14, smoke: true, spread: 0.3 });
      ctx.vfx.ring(ctx.at(e.x, e.y, 0.03), 0.1, 0.9, '#cfeaff', 8, 0.6);
      return false;
    },
    frame: (t, camera) => {
      lookPoint(camera, look);
      snow.frame(t, look);
      (rimeMat.uniforms.uTime as { value: number }).value = t;
    },
    dispose: () => {
      sky.restore();
      snow.dispose();
      geo.dispose();
      rimeMat.dispose();
    },
  };
};
